import { createListCollection, useAsyncList } from '@ark-ui/react/collection';
import { Combobox, useCombobox } from '@ark-ui/react/combobox';
import { Portal } from '@ark-ui/react/portal';
import cn from 'classnames';
import { FocusEvent, startTransition } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';

import {
  deleteQueryFromRecentSearches,
  getRecentSearchQueries,
} from 'src/utils/recentSearchesFromLocalStorage';

import { TypeaheadSearchResult } from 'src/types/model';

import ArrowForward from 'assets/icons/arrow-forward.svg';
import CloseIcon from 'assets/icons/close.svg';
import MagnifyingGlass from 'assets/icons/magnifying-glass.svg';

import Text from 'components/BaseHelpers/Text';

import styles from './SearchCombobox.module.scss';

export type SearchComboboxProps = {
  className?: string;
  placeholder: string;
  inputValue?: string;
  hideClearButton?: boolean;
  clearInputOnTrigger?: boolean;
  typeaheadResultsCount: number;
  recentSearchesLabel: string;
  getTypeaheadResult: (
    query: string,
    count: number
  ) => Promise<{ results: TypeaheadSearchResult[] }>;
  onInputValueChange?: (query: string) => void;
  onSubmit: (query: string) => void;
  onSelect: (details: Combobox.SelectionDetails) => void;
  localStorageKey: string;
};

const SearchCombobox = (props: SearchComboboxProps): JSX.Element => {
  const {
    placeholder,
    inputValue,
    hideClearButton = false,
    clearInputOnTrigger = false,
    typeaheadResultsCount,
    recentSearchesLabel,
    className,
    getTypeaheadResult,
    localStorageKey,
    onInputValueChange,
    onSubmit,
    onSelect,
  } = props;
  const dictionary = useDictionary();

  const list = useAsyncList<TypeaheadSearchResult>({
    async load({ filterText, signal }) {
      if (signal?.aborted) return { items: [] };

      if (filterText === localStorageKey) {
        return {
          items: getRecentSearchQueries(localStorageKey),
        };
      }

      if (filterText.length < 3) {
        return { items: [] };
      }

      const data = await getTypeaheadResult(filterText, typeaheadResultsCount);

      return { items: data.results };
    },
  });

  const collection = createListCollection({
    items: list.items,
    itemToString: (item) => item.title,
    itemToValue: (item) => item.value,
  });

  const handleInputChange = (details: Combobox.InputValueChangeDetails) => {
    if (details.reason === 'input-change') {
      onInputValueChange?.(details.inputValue);
      startTransition(() => {
        list.setFilterText(details.inputValue);
      });
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (!event.currentTarget.value) {
      startTransition(() => {
        list.setFilterText(localStorageKey);
      });
    }
  };

  const isRecentSearches = list.filterText === localStorageKey;

  const combobox = useCombobox({
    collection,
    ...(inputValue !== undefined ? { inputValue } : {}),
    onInputValueChange: handleInputChange,
    selectionBehavior: 'preserve',
    openOnClick: true,
    closeOnSelect: true,
    onSelect,
  });

  const currentInputValue = inputValue ?? combobox.inputValue ?? '';
  const hasInputValue = currentInputValue.length > 0;

  const handleClearInput = () => {
    onInputValueChange?.('');
    startTransition(() => {
      list.setFilterText('');
    });
    combobox.clearValue();
  };

  return (
    <div className={cn(styles.main, className)}>
      <Combobox.RootProvider value={combobox}>
        <Combobox.Control className={styles.control}>
          <Combobox.Label aria-label="Search" className={styles['label-wrapper']}>
            <MagnifyingGlass className={styles['magnifying-glass']} />
          </Combobox.Label>
          <Combobox.Input
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const query = event.currentTarget.value;

                // Enter on highlighted item should navigate to that item, not trigger a search
                if (combobox.highlightedItem) {
                  return;
                } else if (query) {
                  onSubmit(query);
                }
                combobox.setOpen(false);
              }
            }}
            placeholder={placeholder}
            className={styles.input}
            onFocus={handleFocus}
          />
          {!hideClearButton && hasInputValue && (
            <button
              className={styles['clear-button']}
              aria-label="Clear input"
              onClick={handleClearInput}
            >
              <CloseIcon className={styles['close-icon']} />
            </button>
          )}
          <button
            className={styles.trigger}
            aria-label={clearInputOnTrigger && hasInputValue ? 'Clear input' : 'Search'}
            onClick={() => {
              if (clearInputOnTrigger && hasInputValue) {
                handleClearInput();
                return;
              }

              const query = (combobox.inputValue ?? '').trim();
              if (!query) {
                return;
              }
              onSubmit(query);
              combobox.setOpen(false);
            }}
          >
            {clearInputOnTrigger && hasInputValue ? (
              <CloseIcon className={styles['close-icon']} />
            ) : (
              <ArrowForward className={styles['arrow-icon']} />
            )}
          </button>
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner style={{ zIndex: 'var(--z-index__global-header-search-input)' }}>
            {collection.items.length > 0 && (
              <Combobox.Content className={styles.content}>
                {isRecentSearches && collection.items.length > 0 && (
                  <Text
                    className={cn(styles['recent-searches-label'], 'body-eyebrow')}
                    field={recentSearchesLabel}
                  />
                )}
                {collection.items.map((result) => (
                  <div className={styles['item-wrapper']} key={result.value}>
                    <Combobox.Item className={styles.item} item={result}>
                      <Combobox.ItemText
                        className={cn(styles['item-text'], 'body-sm', {
                          [styles['recent-searches-item']]: isRecentSearches,
                        })}
                      >
                        <Text field={result.title} />
                        {!isRecentSearches && <ArrowForward className={styles['item-arrow']} />}
                      </Combobox.ItemText>
                    </Combobox.Item>
                    {isRecentSearches && (
                      <button
                        className={cn(styles['delete-button'], 'body-eyebrow')}
                        tabIndex={-1}
                        onClick={() => {
                          deleteQueryFromRecentSearches(
                            { title: result.title, value: result.value },
                            localStorageKey
                          );
                          list.reload();
                        }}
                      >
                        <Text field={dictionary.deleteLabel} />
                      </button>
                    )}
                  </div>
                ))}
              </Combobox.Content>
            )}
          </Combobox.Positioner>
        </Portal>
      </Combobox.RootProvider>
    </div>
  );
};

export default SearchCombobox;
